import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../../../contexts/SettingsContext';
import type { UserRole } from '../../../../types/settings';

const schema = z.object({
  name: z.string().min(1, 'Role name is required'),
  description: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const PERMISSION_CATEGORIES = {
  dashboard:   { label: 'Dashboard',    perms: { view: 'View overview & stats' } },
  players:     { label: 'Players',      perms: { read: 'View', create: 'Register', update: 'Edit', delete: 'Remove', approve: 'Approve' } },
  scores:      { label: 'Scores',       perms: { read: 'View', create: 'Enter', update: 'Edit', delete: 'Delete' } },
  partners:    { label: 'Partners',     perms: { read: 'View', create: 'Add', update: 'Edit', delete: 'Remove', approve: 'Approve' } },
  events:      { label: 'Events',       perms: { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete' } },
  financials:  { label: 'Finance',      perms: { read: 'View', create: 'Add', update: 'Edit', delete: 'Delete', approve: 'Approve' } },
  content:     { label: 'Content',      perms: { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete' } },
  tournaments: { label: 'Tournaments',  perms: { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete' } },
  courses:     { label: 'Courses',      perms: { read: 'View', create: 'Add', update: 'Edit', delete: 'Delete' } },
  press:       { label: 'Press',        perms: { read: 'View', create: 'Add', update: 'Edit', delete: 'Delete' } },
  analytics:   { label: 'Analytics',    perms: { view: 'View analytics & traffic' } },
  settings:    { label: 'Settings',     perms: { read: 'View', update: 'Update' } },
  users:       { label: 'Users',        perms: { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete', assign_roles: 'Assign Roles' } },
  account:     { label: 'My Account',   perms: { manage: 'Access & close account' } },
};

type View = 'list' | 'form';

export default function MobileSettingsUserRoles() {
  const { state, addUserRole, updateUserRole, deleteUserRole } = useSettings();
  const [view, setView] = useState<View>('list');
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [selectedPerms, setSelectedPerms] = useState<Record<string, Record<string, boolean>>>({});

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '' },
  });

  const initPerms = (role?: UserRole) => {
    const p: Record<string, Record<string, boolean>> = {};
    Object.keys(PERMISSION_CATEGORIES).forEach(cat => {
      p[cat] = {};
      Object.keys(PERMISSION_CATEGORIES[cat as keyof typeof PERMISSION_CATEGORIES].perms).forEach(perm => {
        p[cat][perm] = role?.permissions?.[cat]?.[perm] ?? false;
      });
    });
    return p;
  };

  const openAdd = () => {
    setEditingRole(null);
    reset({ name: '', description: '' });
    setSelectedPerms(initPerms());
    setView('form');
  };

  const openEdit = (role: UserRole) => {
    setEditingRole(role);
    reset({ name: role.name, description: role.description ?? '' });
    setSelectedPerms(initPerms(role));
    setView('form');
  };

  const togglePerm = (cat: string, perm: string) => {
    setSelectedPerms(prev => ({
      ...prev,
      [cat]: { ...prev[cat], [perm]: !prev[cat][perm] },
    }));
  };

  const onSubmit = async (data: FormData) => {
    const payload = { ...data, permissions: selectedPerms };
    if (editingRole) {
      await updateUserRole({ ...editingRole, ...payload });
    } else {
      await addUserRole(payload);
    }
    setView('list');
    reset();
  };

  const handleDelete = async (id: string, name: string) => {
    if (name.toLowerCase() === 'admin') return;
    if (!window.confirm(`Delete role "${name}"?`)) return;
    await deleteUserRole(id);
  };

  if (view === 'form') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-golf-gray text-base">{editingRole ? 'Edit Role' : 'New Role'}</h3>
          <button onClick={() => setView('list')} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role Name *</label>
            <input type="text" {...register('name')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <input type="text" {...register('description')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Permissions</p>
            <div className="space-y-3">
              {Object.entries(PERMISSION_CATEGORIES).map(([cat, { label, perms }]) => (
                <div key={cat} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">{label}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(perms).map(([perm, permLabel]) => {
                      const active = selectedPerms[cat]?.[perm] ?? false;
                      return (
                        <button
                          key={perm}
                          type="button"
                          onClick={() => togglePerm(cat, perm)}
                          className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                            active ? 'bg-golf-orange text-white' : 'bg-white text-gray-500 border border-gray-200'
                          }`}
                        >
                          {permLabel}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setView('list')} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-medium disabled:opacity-50">
              {isSubmitting ? 'Saving...' : (editingRole ? 'Update' : 'Add Role')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        onClick={openAdd}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-golf-orange text-white rounded-xl text-sm font-medium"
      >
        <PlusIcon className="w-4 h-4" />
        Add Role
      </button>

      {state.userRoles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
          <UserGroupIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No roles configured</p>
        </div>
      ) : (
        state.userRoles.map(role => {
          const isAdmin = role.name.toLowerCase() === 'admin';
          const permCount = Object.values(role.permissions ?? {}).reduce((n, cat) => {
            return n + Object.values(cat as Record<string, boolean>).filter(Boolean).length;
          }, 0);

          return (
            <div key={role.id} className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-golf-gray">{role.name}</p>
                    {isAdmin && <span className="text-xs bg-golf-orange/10 text-golf-orange px-2 py-0.5 rounded-full">Admin</span>}
                  </div>
                  {role.description && <p className="text-xs text-gray-400 mt-0.5">{role.description}</p>}
                  <p className="text-xs text-gray-400 mt-1">{permCount} permission{permCount !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(role)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-golf-orange bg-golf-orange/10 rounded-lg">
                  <PencilIcon className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(role.id, role.name)}
                  disabled={isAdmin}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
